import api from '../../api/client';

// --- Cases ---

export interface AdmCaseTransition {
  id: string;
  case_id: string;
  from_status: string;
  to_status: string;
  comment: string | null;
  performed_by_user_id: string | null;
  performed_by_name: string | null;
  created_at: string;
}

export interface AdmCase {
  id: string;
  organization_id: string | null;
  case_number: string;
  title: string;
  description: string | null;
  case_type: string;
  status: string;
  priority: string;
  department: string | null;
  assigned_to_user_id: string | null;
  assigned_to_name: string | null;
  due_date: string | null;
  resolved_at: string | null;
  closed_at: string | null;
  created_by_user_id: string | null;
  created_at: string;
  updated_at: string;
  transitions?: AdmCaseTransition[];
}

export interface AdmCaseListParams {
  status?: string;
  case_type?: string;
  priority?: string;
  assigned_to_user_id?: string;
  skip?: number;
  limit?: number;
}

export async function listCases(params?: AdmCaseListParams) {
  const res = await api.get<AdmCase[]>('/administration/cases', { params });
  return res.data;
}

export async function getCase(id: string) {
  const res = await api.get<AdmCase>(`/administration/cases/${id}`);
  return res.data;
}

export async function createCase(data: Partial<AdmCase>) {
  const res = await api.post<AdmCase>('/administration/cases', data);
  return res.data;
}

export async function updateCase(id: string, data: Partial<AdmCase>) {
  const res = await api.put<AdmCase>(`/administration/cases/${id}`, data);
  return res.data;
}

export async function transitionCase(id: string, toStatus: string, comment?: string) {
  const res = await api.post<AdmCase>(`/administration/cases/${id}/transition`, { target_status: toStatus, comment });
  return res.data;
}

export async function getCaseHistory(id: string) {
  const res = await api.get<AdmCaseTransition[]>(`/administration/cases/${id}/history`);
  return res.data;
}

// --- Documents ---

export interface AdmDocument {
  id: string;
  organization_id: string | null;
  title: string;
  description: string | null;
  category: string;
  file_path: string | null;
  file_size: number | null;
  mime_type: string | null;
  version: number;
  retention_years: number | null;
  tags: string[];
  created_by_user_id: string | null;
  created_by_name: string | null;
  created_at: string;
  updated_at: string;
}

export interface AdmDocumentListParams {
  category?: string;
  search?: string;
  skip?: number;
  limit?: number;
}

export async function listDocuments(params?: AdmDocumentListParams) {
  const res = await api.get<AdmDocument[]>('/administration/documents', { params });
  return res.data;
}

export async function getDocument(id: string) {
  const res = await api.get<AdmDocument>(`/administration/documents/${id}`);
  return res.data;
}

export async function createDocument(data: Partial<AdmDocument>) {
  const res = await api.post<AdmDocument>('/administration/documents', data);
  return res.data;
}

export async function deleteDocument(id: string) {
  const res = await api.delete(`/administration/documents/${id}`);
  return res.data;
}

// --- Council ---

export interface AdmCouncilItem {
  id: string;
  organization_id: string | null;
  title: string;
  description: string | null;
  item_type: string;
  session_date: string | null;
  agenda_number: string | null;
  status: string;
  decision_text: string | null;
  vote_for: number | null;
  vote_against: number | null;
  vote_abstain: number | null;
  related_case_id: string | null;
  documents: unknown[];
  created_by_user_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface AdmCouncilListParams {
  status?: string;
  skip?: number;
  limit?: number;
}

export async function listCouncilItems(params?: AdmCouncilListParams) {
  const res = await api.get<AdmCouncilItem[]>('/administration/council', { params });
  return res.data;
}

export async function getCouncilItem(id: string) {
  const res = await api.get<AdmCouncilItem>(`/administration/council/${id}`);
  return res.data;
}

export async function createCouncilItem(data: Partial<AdmCouncilItem>) {
  const res = await api.post<AdmCouncilItem>('/administration/council', data);
  return res.data;
}

export async function updateCouncilItem(id: string, data: Partial<AdmCouncilItem>) {
  const res = await api.put<AdmCouncilItem>(`/administration/council/${id}`, data);
  return res.data;
}

export async function recordCouncilDecision(id: string, data: { decision_text: string; vote_for: number; vote_against: number; vote_abstain: number }) {
  const res = await api.post<AdmCouncilItem>(`/administration/council/${id}/decision`, data);
  return res.data;
}

// --- Employees ---

export interface AdmEmployee {
  id: string;
  organization_id: string | null;
  user_id: string | null;
  first_name: string;
  last_name: string;
  position_title: string | null;
  department: string | null;
  employment_type: string;
  employment_percentage: number;
  start_date: string | null;
  end_date: string | null;
  salary_class: string | null;
  salary_step: number | null;
  status: string;
  created_at: string;
  updated_at: string;
}

export interface AdmEmployeeListParams {
  department?: string;
  status?: string;
  employment_type?: string;
  skip?: number;
  limit?: number;
}

export async function listEmployees(params?: AdmEmployeeListParams) {
  const res = await api.get<AdmEmployee[]>('/administration/employees', { params });
  return res.data;
}

export async function getEmployee(id: string) {
  const res = await api.get<AdmEmployee>(`/administration/employees/${id}`);
  return res.data;
}

export async function createEmployee(data: Partial<AdmEmployee>) {
  const res = await api.post<AdmEmployee>('/administration/employees', data);
  return res.data;
}

export async function updateEmployee(id: string, data: Partial<AdmEmployee>) {
  const res = await api.put<AdmEmployee>(`/administration/employees/${id}`, data);
  return res.data;
}
